import {
  Sun,
  TvMinimal,
  CircleUser,
  RefreshCcw,
  Download,
  ArchiveRestore,
  ShieldCheck,
  Scale,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { AppExperienceIcon } from '@/src/renderer/components/icons/AppExperienceIcon'
import { AccountDataIcon } from '@/src/renderer/components/icons/AccountDataIcon'
import { HelpAboutIcon } from '@/src/renderer/components/icons/HelpAboutIcon'

/**
 * Icon-component contract — broad enough to accept both `lucide-react`
 * icons (which take `size`/`strokeWidth`/`className`) and our own branded
 * SVG components.
 */
export type SettingsIcon = ComponentType<{
  size?: number | string
  strokeWidth?: number | string
  className?: string
}>

export interface SubNavEntry {
  /** Stable id, used in localStorage and the navigation hook. */
  id: string
  /** i18n key under `settings.subNav.*`. */
  i18nKey: string
  icon: SettingsIcon
  /**
   * Nur in Electron sichtbar. Im Web fällt der Eintrag WEG (siehe
   * {@link visibleTree}) — er wird dort nicht gestubbt: Seit dem MVP-Schnitt
   * gilt „keine Seite in den Einstellungen zeigt ‚Bald verfügbar'", und ein
   * plattformbedingter Stub wäre genau das.
   */
  electronOnly?: boolean
}

export interface TopNavEntry {
  id: string
  i18nKey: string
  icon: SettingsIcon
  /** SubNav items rendered in the left sidebar when this TopNav is active. */
  subNav: ReadonlyArray<SubNavEntry>
}

/**
 * Source of truth for the Settings navigation tree. Order here is the order
 * shown in the UI. Adding a new SubNav: append an entry and ship its
 * i18n key under `settings.subNav.*` in both locales.
 *
 * Uses `as const satisfies` so the literal `id` strings survive — that
 * lets {@link TopNavId}, {@link SubNavId} and {@link SlotId} infer as
 * tight unions instead of plain `string`.
 */
export const SETTINGS_TREE = [
  {
    id: 'account-data',
    i18nKey: 'settings.topNav.accountData',
    icon: AccountDataIcon,
    subNav: [
      { id: 'account-profile', i18nKey: 'settings.subNav.accountProfile', icon: CircleUser },
      { id: 'sync', i18nKey: 'settings.subNav.sync', icon: RefreshCcw },
      // Backups liest und schreibt der Hauptprozess (#158) — im Web gibt es sie noch nicht.
      { id: 'backups', i18nKey: 'settings.subNav.backups', icon: ArchiveRestore, electronOnly: true },
      { id: 'import', i18nKey: 'settings.subNav.import', icon: Download },
      { id: 'data-privacy', i18nKey: 'settings.subNav.dataPrivacy', icon: ShieldCheck },
    ],
  },
  {
    id: 'app-experience',
    i18nKey: 'settings.topNav.appExperience',
    icon: AppExperienceIcon,
    subNav: [
      { id: 'appearance', i18nKey: 'settings.subNav.appearance', icon: Sun },
      // Auch im Web (seit v0.6.9): Dort steht der Sound-Schalter. Die
      // Fenster-Modi (Dock/Menubar/Tray) und das globale Kürzel blendet die
      // Seite im Web selbst aus.
      { id: 'app-behavior', i18nKey: 'settings.subNav.appBehavior', icon: TvMinimal },
    ],
  },
  {
    id: 'help-about',
    i18nKey: 'settings.topNav.helpAbout',
    icon: HelpAboutIcon,
    subNav: [
      // Die Lizenzen der mitgelieferten Fremdbibliotheken — die einzige Seite,
      // die die Gruppe nach dem MVP-Schnitt noch trägt. Sie bleibt hier
      // stehen: Lizenzen sind eine Pflichtangabe und gehören unter „Hilfe &
      // Info", nicht neben das Erscheinungsbild. Und die Gruppe hält den
      // Platz für das, was der Schnitt genommen hat und später zurückkommt
      // (Changelog, Über die App).
      { id: 'open-source', i18nKey: 'settings.subNav.openSource', icon: Scale },
    ],
  },
] as const satisfies ReadonlyArray<TopNavEntry>

// ---------------------------------------------------------------------------
// Derived types — kept generic over `typeof SETTINGS_TREE` so adding a new
// TopNav or SubNav to the literal above automatically widens the unions.
// ---------------------------------------------------------------------------

/** Union of all TopNav ids that exist in {@link SETTINGS_TREE}. */
export type TopNavId = (typeof SETTINGS_TREE)[number]['id']

/** Union of every SubNav id across all TopNavs. */
export type SubNavId = (typeof SETTINGS_TREE)[number]['subNav'][number]['id']

/**
 * Template-literal of every theoretically valid `<topNav>/<subNav>` pair.
 * Looser than a "real combinations only" mapped type, but trivial to
 * understand and good enough — every real slot id matches this pattern,
 * and impossible pairs (`'help-about/appearance'`) are still rejected at
 * runtime by {@link findSubNav}.
 */
export type SlotId = `${TopNavId}/${SubNavId}`

// ---------------------------------------------------------------------------
// Runtime helpers — accept `string` at the boundary (localStorage etc.) and
// narrow into the tight union types via type guards.
// ---------------------------------------------------------------------------

const TOP_NAV_IDS: ReadonlySet<TopNavId> = new Set(
  SETTINGS_TREE.map((t) => t.id),
)

const SUB_NAV_IDS: ReadonlySet<SubNavId> = new Set(
  SETTINGS_TREE.flatMap((t) => t.subNav.map((s) => s.id)),
)

export function isTopNavId(value: unknown): value is TopNavId {
  return typeof value === 'string' && TOP_NAV_IDS.has(value as TopNavId)
}

export function isSubNavId(value: unknown): value is SubNavId {
  return typeof value === 'string' && SUB_NAV_IDS.has(value as SubNavId)
}

/** Build the canonical `<topNav>/<subNav>` slot id. */
export function slotId(topNavId: TopNavId, subNavId: SubNavId): SlotId {
  return `${topNavId}/${subNavId}` as SlotId
}

/** Default landing — the first top-nav "Profil & Daten > Profil". */
export const DEFAULT_NAV: { topNavId: TopNavId; subNavId: SubNavId } = {
  topNavId: 'account-data',
  subNavId: 'account-profile',
}

/**
 * Look up a TopNav entry by id. Returns undefined if not found — callers
 * that need a fallback should default to {@link SETTINGS_TREE}[0].
 */
export function findTopNav(id: string): (typeof SETTINGS_TREE)[number] | undefined {
  return SETTINGS_TREE.find((t) => t.id === id)
}

/** Look up a SubNav entry inside a given TopNav. */
export function findSubNav(topNavId: string, subNavId: string) {
  return findTopNav(topNavId)?.subNav.find((s) => s.id === subNavId)
}

/**
 * Der Baum, wie ihn DIESE Plattform zeigt.
 *
 * In Electron ist es der ganze; im Web fallen die `electronOnly`-Einträge
 * weg. **Ausgeblendet, nicht gestubbt:** Seit dem MVP-Schnitt zeigt keine
 * Seite der Einstellungen „Bald verfügbar", und ein Eintrag, der im Web nur
 * auf den Rückfall zeigte, wäre genau das.
 *
 * Der Baum selbst bleibt vollständig — er trägt die Typen (`TopNavId`,
 * `SubNavId`, `SlotId`), und die dürfen nicht je Plattform anders aussehen.
 */
export function visibleTree(platform: 'electron' | 'web') {
  if (platform === 'electron') return SETTINGS_TREE
  return SETTINGS_TREE.map((top) => ({
    ...top,
    subNav: top.subNav.filter((sub: SubNavEntry) => !sub.electronOnly),
  })).filter((top) => top.subNav.length > 0)
}

/**
 * Wie {@link findSubNav}, aber nur unter dem, was diese Plattform zeigt —
 * für den Fall, dass ein im Web unsichtbarer Eintrag aus dem `localStorage`
 * zurückkommt (siehe `usePreferencesNavigation`).
 */
export function findVisibleSubNav(topNavId: string, subNavId: string, platform: 'electron' | 'web') {
  return visibleTree(platform)
    .find((t) => t.id === topNavId)
    ?.subNav.find((s) => s.id === subNavId)
}
